import React, { useState } from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Paper, CircularProgress, CardActions
} from "@mui/material";
import {
    updateServerSettings,
    downloadBackup,
    restoreBackup,
    createBackup,
    wipeServer, jobServer
} from "../http/ServerAPI";
import {LOG_ROUTE, SERVER_PLUGINS_ROUTE, SERVER_SETTINGS, SERVERS_ROUTE} from "../utils/consts";
import BreadCrumbs from "../components/Breadcrumbs";
import Container from "@mui/material/Container";
import ServerSettingsForm from "../components/ServerSettingsForm";
import ServerBackups from "../components/ServerBackups";
import ServerStatsChart from "../components/ServerStatsChart";
import {ListAlt, PlayArrow, RocketLaunch, Settings, Source, Stop, TipsAndUpdates} from "@mui/icons-material";
import {getApiErrorMessage} from "../http/apiError";
import { useServerSettingsData } from "../hooks/useServerSettingsData";
import { useSnackbar } from "../components/snackbar/SnackbarProvider";

const ServerSettings = () => {
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const { server_id } = useParams();
    const { settings, server, stats, backups } = useServerSettingsData(server_id);
    const [dialog, setDialog] = useState({ open: false, action: null });
    const [downloading, setDownloading] = useState(false);
    const [confirm, setConfirm] = useState({ open: false, action: null, text: "", serverId: null });
    const [loadingButton, setLoadingButton] = useState(null);

    const hrefs = [
        { href: SERVERS_ROUTE, name: "Список серверов" },
    ];

    const handleConfirm = (text, action, serverId) => {
        setConfirm({ open: true, action, text, serverId });
    };

    const handleConfirmClose = () => {
        setConfirm({ open: false, action: null, text: "", serverId: null });
    };

    const handleConfirmedAction = async () => {
        if (!confirm.action) return;
        setLoadingButton(confirm.action);
        handleConfirmClose();

        try {
            if (confirm.action === "wipe") {
                await wipeServer(confirm.serverId);
                await server.reload();
            } else {
                await jobServer(confirm.serverId, confirm.action);
                await server.reload();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingButton(null);
        }
    };

    const handleChange = (field, value) => {
        settings.setData(prev => ({ ...prev, [field]: value }));
    };

    const openDialog = (action) => {
        setDialog({ open: true, action });
    };

    const closeDialog = () => {
        setDialog({ open: false, action: null });
    };

    const confirmDialog = async () => {
        closeDialog();
        try {
            if (dialog.action === "save") {
                await updateServerSettings(server_id, settings.data);
                await settings.reload();
                snackbar.success('Настройки сохранены');
            }
            if (dialog.action === "backup") {
                await createBackup(server_id);
                snackbar.success('Бэкап создан');
            }
            if (dialog.action === "restore") {
                await restoreBackup(server_id);
                snackbar.success('Восстановление запущено');
            }
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, 'Ошибка выполнения действия'));
        }
        if (dialog.action === "download") {
            setDownloading(true);
            try {
                await downloadBackup(server_id, setDownloading); // передаём setDownloading
            } catch (e) {
                snackbar.error(getApiErrorMessage(e, 'Ошибка при скачивании'));
            } finally {
                setDownloading(false);
            }
        }
    };

    const serverData = server.data || {};
    const settingsData = settings.data;
    const isRunning = serverData.status_id === 3;
    const isStopped = serverData.status_id === 4 || serverData.status_id === 8;
    const isStarting = serverData.status_id === 5;


    if (settings.loading || !settingsData) return <Typography>Загрузка настроек...</Typography>;

    return (
        <Container maxWidth="xl">
            <Box p={3}>
                <Box sx={{ width: '100%', marginBottom: 3 }}>
                    <BreadCrumbs hrefs={hrefs} name='Настройки сервера' />
                </Box>
                <Typography variant="h4" mb={3}>Настройки сервера</Typography>
                <Grid container spacing={2}>
                    {/* Основные настройки */}
                    <Grid item xs={12} md={6}>
                        <ServerSettingsForm settings={settingsData} handleChange={handleChange} openDialog={openDialog} />
                    </Grid>

                    {/* Бэкапы и графики */}
                    <Grid item xs={12} md={6}>
                        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                            <Typography variant="h6" gutterBottom>Управление сервером</Typography>
                            <CardActions sx={{ flexWrap: 'wrap', justifyContent: 'center' }}>
                                {isStopped && (
                                    <Button size="small" color="success" startIcon={loadingButton === "start" ? <CircularProgress size={20} /> : <PlayArrow />}
                                            onClick={() => handleConfirm("Запустить сервер?", "start", serverData.server_id)} disabled={!!loadingButton}>
                                        Старт
                                    </Button>
                                )}
                                {isRunning && (
                                    <Button size="small" color="error" startIcon={loadingButton === "stop" ? <CircularProgress size={20} /> : <Stop />}
                                            onClick={() => handleConfirm("Остановить сервер?", "stop", serverData.server_id)} disabled={!!loadingButton}>
                                        Стоп
                                    </Button>
                                )}
                                <Button size="small" color="warning" startIcon={loadingButton === "update" ? <CircularProgress size={20} /> : <TipsAndUpdates />}
                                        onClick={() => handleConfirm("Обновить сервер?", "update", serverData.server_id)} disabled={!!loadingButton}>
                                    Обновить
                                </Button>
                                <Button size="small" color="info" startIcon={loadingButton === "wipe" ? <CircularProgress size={20} /> : <RocketLaunch />}
                                        onClick={() => handleConfirm("Выполнить вайп сервера?", "wipe", serverData.server_id)} disabled={!!loadingButton}>
                                    Вайп
                                </Button>
                                <Button size="small" color="primary" startIcon={<Source />}
                                        onClick={() => navigate(SERVER_PLUGINS_ROUTE + '/' + serverData.server_id)}>
                                    Плагины
                                </Button>
                                <Button size="small" color="secondary" startIcon={<ListAlt />}
                                        onClick={() => navigate(LOG_ROUTE + '/' + serverData.server_id)}>
                                    Логи
                                </Button>
                            </CardActions>
                        </Paper>

                        <ServerBackups backups={backups.data} downloading={downloading} openDialog={openDialog} />

                        <ServerStatsChart stats={stats.data} />
                    </Grid>
                </Grid>
                {/* Подтверждение */}
                <Dialog open={dialog.open} onClose={closeDialog}>
                    <DialogTitle>Подтвердите действие</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Вы уверены, что хотите {dialog.action === "save" ? "сохранить изменения" : dialog.action === "backup" ? "создать бэкап" : dialog.action === "restore" ? "восстановить сервер" : "скачать бэкап"}?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialog}>Отмена</Button>
                        <Button onClick={confirmDialog} autoFocus>Подтвердить</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={confirm.open} onClose={handleConfirmClose}>
                    <DialogTitle>Подтверждение действия</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {confirm.text}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleConfirmClose}>Отмена</Button>
                        <Button onClick={handleConfirmedAction} color="primary" autoFocus>Подтвердить</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Container>
    );
};

export default ServerSettings;
